import { createShopifyIssuedVoucherAdapter } from './loyalty-cart-shopify-adapter.js?v=m5-20261002-stacking';
import { readNativeLoyalty } from './loyalty-native-reader.js?v=20261005-rested-operational';
import { parseVoucherCatalogue } from './loyalty-voucher-catalogue.js?v=m6-20261002-catalogue';
import { cartEarningKey, readCartEarningQuote, readEarningCartElement } from './loyalty-cart-earnings.js?v=20261005-rested-operational';
import { captureRewardViewport, restoreRewardViewport } from './loyalty-panel-viewport.js?v=20261017-flow-anchor';
import { captureRewardPanelMotion, revealRewardPanel, cancelRewardPanelMotion } from './loyalty-panel-motion.js?v=20261017-spring-reveal';
import { resolveVoucherOwnershipDisplay } from './loyalty-voucher-ownership.js?v=20261012-owner-display';
import { syncTierShipping } from './cart-tier-shipping.js?v=20261012-owner-display';

const locale = document.documentElement.lang || 'en-GB';
const numberFormatter = new Intl.NumberFormat(locale);

const setText = (root, selector, value) => {
  const element = root?.querySelector(selector);
  if (element) element.textContent = value;
};

const safeInteger = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isSafeInteger(number) ? number : fallback;
};

const safeNonNegativeInteger = (value, fallback = 0) => Math.max(0, safeInteger(value, fallback));
const previewStates = new Set(['collapsed', 'expanded', 'applied']);
/* HEADING STORYBOARD
 *   0ms  old label softly fades/defocuses; new label resolves in place
 * 220ms  outgoing label is gone
 * 220ms  new label is fully sharp; temporary layer is removed
 * No travel, stagger or initial-load animation.
 */
const headingMotion = Object.freeze({ exitMs: 220, enterMs: 220,
  blurPx: 3, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
const nativeBagEndpoint = '/apps/sigma-stock-alerts/loyalty/v1/native/me';
const issuedCodePattern = /^SR-[A-Za-z0-9_-]{1,128}$/i;
const maxAppliedVoucherCount = 5;
const nativeSnapshotFreshMs = 5 * 60_000;
// Browser timers clamp larger delays. One retry at the server deadline remains
// bounded without treating a long Retry-After as permission to poll early.
const maximumAutomaticRetryMs = 2_147_000_000;

const codeIdentity = (code) => code.toLocaleLowerCase('en-GB');

const readAppliedVouchers = (dataset) => {
  const hasCodes = Object.hasOwn(dataset, 'appliedVoucherCodes');
  const hasAllocations = Object.hasOwn(dataset, 'appliedVouchers');
  const declaredTotal = Number(dataset.appliedVoucherValueMinor);

  if (hasCodes || hasAllocations) {
    if (!hasCodes || !hasAllocations) return [];

    let codes;
    let allocations;
    try {
      codes = JSON.parse(dataset.appliedVoucherCodes);
      allocations = JSON.parse(dataset.appliedVouchers);
    } catch {
      return [];
    }

    if (
      !Array.isArray(codes)
      || !Array.isArray(allocations)
      || codes.length !== allocations.length
      || codes.length > maxAppliedVoucherCount
      || !Number.isSafeInteger(declaredTotal)
      || declaredTotal < 0
    ) return [];

    const codeIds = new Set();
    for (const code of codes) {
      if (typeof code !== 'string' || !issuedCodePattern.test(code) || codeIds.has(codeIdentity(code))) {
        return [];
      }
      codeIds.add(codeIdentity(code));
    }

    const allocationsByCode = new Map();
    let allocationTotal = 0;
    for (const allocation of allocations) {
      if (
        !allocation
        || typeof allocation !== 'object'
        || Array.isArray(allocation)
        || typeof allocation.code !== 'string'
        || !issuedCodePattern.test(allocation.code)
        || !Number.isSafeInteger(allocation.valueMinor)
        || allocation.valueMinor <= 0
      ) return [];
      const identity = codeIdentity(allocation.code);
      if (allocationsByCode.has(identity)) return [];
      allocationTotal += allocation.valueMinor;
      if (!Number.isSafeInteger(allocationTotal)) return [];
      allocationsByCode.set(identity, allocation);
    }

    if (
      allocationTotal !== declaredTotal
      || allocationsByCode.size !== codeIds.size
      || codes.some((code) => !allocationsByCode.has(codeIdentity(code)))
    ) return [];

    return codes.map((code) => ({
      code,
      valueMinor: allocationsByCode.get(codeIdentity(code)).valueMinor,
    }));
  }

  const legacyCode = dataset.appliedVoucherCode || '';
  if (
    issuedCodePattern.test(legacyCode)
    && Number.isSafeInteger(declaredTotal)
    && declaredTotal > 0
  ) return [{ code: legacyCode, valueMinor: declaredTotal }];
  return [];
};

const writeAppliedVouchers = (dataset, vouchers) => {
  const total = vouchers.reduce((sum, voucher) => sum + voucher.valueMinor, 0);
  dataset.appliedVoucherCodes = JSON.stringify(vouchers.map(({ code }) => code));
  dataset.appliedVouchers = JSON.stringify(vouchers);
  dataset.appliedVoucherValueMinor = String(total);
  dataset.appliedVoucherCode = vouchers.length === 1 ? vouchers[0].code : '';
};

const isNativeBalance = (snapshot) => snapshot?.schemaVersion === 1
  && snapshot.provider === 'loyaltylion'
  && snapshot.available === true
  && snapshot.stale !== true
  && Number.isSafeInteger(snapshot.approvedPoints)
  && snapshot.currencyCode === 'GBP';

const previewRewards = [
  { code: 'preview-250', points: 250, discountMinor: 300, affordable: true },
  { code: 'preview-500', points: 500, discountMinor: 500, affordable: true },
  { code: 'preview-1000', points: 1000, discountMinor: 1000, affordable: true },
];

const formatMoney = (minor, currencyCode, trimWhole = false) => {
  const numericMinor = Number(minor);
  const isWhole = Number.isInteger(numericMinor) && numericMinor % 100 === 0;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode || 'GBP',
      ...(trimWhole && isWhole ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : {}),
    }).format(numericMinor / 100);
  } catch {
    return `£${(numericMinor / 100).toFixed(trimWhole && isWhole ? 0 : 2)}`;
  }
};

const formatRewardMoney = (minor, currencyCode) => formatMoney(minor, currencyCode, true);

class SigmaLoyaltySummary extends HTMLElement {
  connectedCallback() {
    if (this.dataset.authenticated !== 'true') return;

    if (!this.hasBoundControls) {
      this.querySelector('[data-loyalty-toggle]')?.addEventListener('click', (event) => {
        const toggle = this.querySelector('[data-loyalty-toggle]');
        this.setExpanded(toggle?.getAttribute('aria-expanded') !== 'true', { animate: event.detail !== 0 || this.pointerBarActivation === true, anchor: true });
      });
      this.querySelector('.loyalty-summary__bar')?.addEventListener('click', (event) => {
        if (!(event.target instanceof Element) || event.target.closest('button, a')) return;
        const toggle = this.querySelector('[data-loyalty-toggle]');
        if (!(toggle instanceof HTMLButtonElement) || toggle.hidden || toggle.disabled) return;
        toggle.focus({ preventScroll: true });
        this.pointerBarActivation = event.detail !== 0;
        try { toggle.click(); } finally { this.pointerBarActivation = false; }
      });
      this.querySelector('[data-loyalty-retry]')?.addEventListener('click', () => this.load());
      this.querySelector('[data-loyalty-remove]')?.addEventListener('click', () => this.requestRemoveReward());
      this.querySelector('[data-loyalty-reward-list]')?.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;
        const appliedTile = event.target.closest('[data-loyalty-readonly-remove]');
        if (appliedTile instanceof HTMLButtonElement) {
          this.removeAppliedVoucher(appliedTile.dataset.loyaltyReadonlyRemove);
          return;
        }
        const button = event.target.closest('[data-loyalty-reward-code]');
        if (button instanceof HTMLButtonElement) this.requestReward(button.dataset.loyaltyRewardCode);
      });
      this.hasBoundControls = true;
    }

    const previewState = this.dataset.previewState;
    if (window.Shopify?.designMode === true && previewStates.has(previewState)) {
      this.renderPreview(previewState);
      return;
    }

    this.isPreview = false;
    this.load();
  }

  disconnectedCallback() {
    this.clearHeadingMotion();
    this.clearRequestTimeout();
    this.nativeRequest?.release();
    this.nativeRequest = null;
    cancelRewardPanelMotion(this);
  }

  setVoucherRedemptionManaged(managed) {
    const wasExpanded = this.querySelector('[data-loyalty-toggle]')?.getAttribute('aria-expanded') === 'true';
    this.voucherRedemptionManaged = managed === true;
    const toggle = this.querySelector('[data-loyalty-toggle]');
    const slot = this.querySelector('[data-loyalty-redemption-slot]');
    if (slot) slot.hidden = true;
    if (toggle) toggle.hidden = this.voucherRedemptionManaged ? false
      : !this.hasNativeVoucherCatalogue && !this.appliedVoucher && (this.rewards?.size || 0) === 0;
    this.setExpanded(wasExpanded);
  }

  clearRequestTimeout() {
    if (this.requestTimeoutId == null) return;
    window.clearTimeout(this.requestTimeoutId);
    this.requestTimeoutId = null;
  }

  async load({ automaticRetry = false, background = false } = {}) {
    this.clearRequestTimeout();
    this.nativeRequest?.release();
    this.nativeRequest = null;
    const restoreVoucherExpansion = this.voucherRedemptionManaged === true
      && this.querySelector('[data-loyalty-toggle]')?.getAttribute('aria-expanded') === 'true';
    const backgroundWithLoadedContent = background && this.hasNativeSnapshot === true;
    if (backgroundWithLoadedContent) this.setAttribute('aria-busy', 'true');
    else this.setLoading();

    const endpoint = this.dataset.endpoint;
    if (endpoint !== nativeBagEndpoint) {
      this.showUnavailable();
      return;
    }

    const requestUrl = new URL(endpoint, window.location.origin);
    const earningCart = readEarningCartElement(this);
    const requestedCartKey = earningCart ? cartEarningKey(earningCart) : null;
    this.requestedCartKey = requestedCartKey;
    const request = readNativeLoyalty({ url: requestUrl.href, cart: earningCart, cartKey: requestedCartKey });
    this.nativeRequest = request;

    try {
      const response = await request.promise;
      const { snapshot } = response;
      if (request !== this.nativeRequest || !this.isConnected) return;
      if (!response.ok || !isNativeBalance(snapshot)) {
        const retryAfterMs = Number.isFinite(response.retryAfterMs)
          ? Math.max(0, response.retryAfterMs) : 0;
        const preserveAfterThrottle = response.status === 429
          && backgroundWithLoadedContent
          && this.renderedCartKey === requestedCartKey
          && Date.now() + retryAfterMs < (this.nativeSnapshotFreshUntil || 0);
        const schedulesAutomaticRetry = response.status === 429
          && !automaticRetry
          && retryAfterMs > 0
          && retryAfterMs <= maximumAutomaticRetryMs;
        if (preserveAfterThrottle) this.showRefreshDeferred();
        else {
          this.showUnavailable(snapshot);
          if (schedulesAutomaticRetry) {
            this.collapsedMessage = 'Updating your loyalty points';
            this.expandedMessage = this.collapsedMessage;
            this.setExpanded(false);
            setText(this, '[data-loyalty-error]', 'Your points are refreshing. You can still check out.');
          }
        }
        this.setAttribute('aria-busy', 'false');
        if (schedulesAutomaticRetry) {
          this.requestTimeoutId = window.setTimeout(() => (
            this.isConnected ? this.load({
              automaticRetry: true,
              background: preserveAfterThrottle,
            }) : undefined
          ), retryAfterMs + 100);
        }
        return;
      }
      const restoredDuringLoad = this.voucherRedemptionManaged === true
        && this.querySelector('[data-loyalty-toggle]')?.getAttribute('aria-expanded') === 'true';
      this.renderSnapshot(snapshot, {
        restoreVoucherExpansion: backgroundWithLoadedContent ? restoredDuringLoad
          : restoreVoucherExpansion || restoredDuringLoad,
      });
    } catch (error) {
      if (request !== this.nativeRequest || !this.isConnected) return;
      this.showUnavailable();
    } finally {
      request.release();
    }
  }

  setLoading() {
    this.setAttribute('aria-busy', 'true');
    this.rewards = new Map();
    this.appliedReward = null;
    this.appliedVoucher = null;
    this.appliedVouchers = [];
    this.earningPoints = null;
    this.nativePurchasePoints = null;
    this.subscriberBonusPoints = 0;
    this.restedBonusPoints = 0;
    this.subscriberBonusActive = false;
    this.subscriberBonusPercent = null;
    const earningMessage = this.querySelector('[data-loyalty-earning-message]');
    if (earningMessage) earningMessage.hidden = true;
    this.hasNativeVoucherCatalogue = false;
    this.lastSavedVouchers = null;
    this.applyVoucherOwnershipDisplay();
    this.collapsedMessage = 'Loading your loyalty points';
    this.expandedMessage = this.collapsedMessage;
    setText(this, '[data-loyalty-reward-label]', 'Choose amount to redeem');
    const remove = this.querySelector('[data-loyalty-remove]');
    if (remove) remove.hidden = true;
    this.setExpanded(false);
    const panel = this.querySelector('[data-loyalty-panel]');
    if (panel) panel.dataset.state = 'loading';
    const toggle = this.querySelector('[data-loyalty-toggle]');
    if (toggle) toggle.hidden = true;
    this.hideFeedback();
  }

  showUnavailable(snapshot = {}) {
    this.hasNativeSnapshot = false;
    this.nativeSnapshotFreshUntil = 0;
    this.renderedCartKey = null;
    setText(this, '[data-loyalty-error]', 'We cannot load your loyalty balance right now. You can still check out normally.');
    const isNewAccount = snapshot.reason === 'not_enrolled';
    this.collapsedMessage = isNewAccount
      ? 'Your rewards account is not available yet'
      : 'Your loyalty balance is unavailable right now';
    this.expandedMessage = this.collapsedMessage;
    this.setExpanded(false);

    const panel = this.querySelector('[data-loyalty-panel]');
    const toggle = this.querySelector('[data-loyalty-toggle]');
    const feedback = this.querySelector('[data-loyalty-feedback]');
    const retry = this.querySelector('[data-loyalty-retry]');
    if (panel) panel.dataset.state = isNewAccount ? 'empty' : 'unavailable';
    this.lastSavedVouchers = null;
    this.applyVoucherOwnershipDisplay();
    this.renderNativeVoucherRewards([]);
    if (toggle) toggle.hidden = !this.appliedVoucher;
    if (feedback) feedback.hidden = isNewAccount;
    if (retry) retry.hidden = isNewAccount;
    this.setAttribute('aria-busy', 'false');
    this.dispatchEvent(new CustomEvent('sigma:loyalty-unavailable', { bubbles: true }));
  }

  showRefreshDeferred() {
    const feedback = this.querySelector('[data-loyalty-feedback]');
    const retry = this.querySelector('[data-loyalty-retry]');
    if (feedback) feedback.hidden = false;
    if (retry) retry.hidden = false;
    setText(this, '[data-loyalty-error]', 'We could not refresh your points yet. Your current loyalty details remain visible and you can still check out.');
    this.setAttribute('aria-busy', 'false');
  }

  renderPreview(state) {
    this.isPreview = true;
    const approvedPoints = 1300;
    this.renderSnapshot({
      available: true,
      approvedPoints,
      currencyCode: this.dataset.currencyCode || 'GBP',
      redemptionAvailable: true,
      rewards: previewRewards,
      preview: true,
    });

    if (state === 'expanded' || state === 'applied') this.setExpanded(true);
    if (state === 'applied') {
      const discountMinor = 500;
      this.applyConfirmedReward({
        code: 'preview-500',
        points: 500,
        discountMinor,
        remainingPoints: approvedPoints - 500,
        estimatedTotalMinor: Math.max(0, safeNonNegativeInteger(this.dataset.cartTotalMinor) - discountMinor),
      });
    }
  }

  renderSnapshot(snapshot, { restoreVoucherExpansion = false } = {}) {
    const viewportAnchor = captureRewardViewport(this);
    this.hasNativeSnapshot = true;
    const wasLoading = this.querySelector('[data-loyalty-panel]')?.dataset.state === 'loading';
    const approvedPoints = safeInteger(snapshot.approvedPoints);
    const hasNegativeBalance = approvedPoints < 0;
    this.approvedPoints = approvedPoints;
    this.lastSavedVouchers = snapshot.savedVouchers;
    const ownership = this.applyVoucherOwnershipDisplay(snapshot.savedVouchers);
    const earningQuote = this.isPreview || ownership.withheldValueMinor > 0 ? null
      : readCartEarningQuote(snapshot.cartEarnings, this.requestedCartKey);
    this.renderedCartKey = this.requestedCartKey;
    this.nativeSnapshotFreshUntil = Date.now() + nativeSnapshotFreshMs;
    if (earningQuote && Number.isSafeInteger(snapshot.cartEarnings?.expiresAtMs)) {
      this.nativeSnapshotFreshUntil = Math.min(
        this.nativeSnapshotFreshUntil,
        snapshot.cartEarnings.expiresAtMs,
      );
    }
    this.earningPoints = earningQuote?.points ?? null;
    this.nativePurchasePoints = earningQuote?.nativePurchasePoints ?? null;
    this.subscriberBonusPoints = earningQuote?.subscriberBonusPoints ?? 0;
    this.restedBonusPoints = earningQuote?.restedBonusPoints ?? 0;
    this.subscriberBonusActive = earningQuote?.subscriberBonusActive === true;
    this.subscriberBonusPercent = this.subscriberBonusActive
      && Number.isSafeInteger(earningQuote?.subscriberBonusPercent)
      ? earningQuote.subscriberBonusPercent : null;
    this.collapsedMessage = this.earningPoints !== null
      ? `Earn ${numberFormatter.format(this.earningPoints)} points`
      : hasNegativeBalance
      ? `Redemption paused · ${numberFormatter.format(Math.abs(approvedPoints))} points to recover`
      : 'Earn points on eligible items';
    this.expandedMessage = hasNegativeBalance
      ? `Redemption paused · ${numberFormatter.format(Math.abs(approvedPoints))} points to recover`
      : `${numberFormatter.format(approvedPoints)} points available`;

    const rewardCount = this.renderRewards(
      snapshot.rewards,
      snapshot.currencyCode,
      this.isPreview === true && snapshot.redemptionAvailable === true && !hasNegativeBalance,
    );
    if (!this.isPreview) this.renderNativeVoucherRewards(snapshot.voucherRewards, ownership);
    const panel = this.querySelector('[data-loyalty-panel]');
    const toggle = this.querySelector('[data-loyalty-toggle]');
    if (panel) panel.dataset.state = 'collapsed';
    if (toggle) toggle.hidden = this.voucherRedemptionManaged ? false
      : rewardCount === 0 && !this.hasNativeVoucherCatalogue && !this.appliedVoucher;
    this.hideFeedback();
    this.setExpanded(restoreVoucherExpansion && this.voucherRedemptionManaged === true, {
      // Restoring a managed panel is continuity, not a user-triggered open.
      // Animating it makes the voucher slot inert and drops result focus.
      animate: wasLoading && !restoreVoucherExpansion,
    });
    this.setAttribute('aria-busy', 'false');

    // A fresh estimate belongs to this confirmed cart, not the point balance.
    // Keep it visible until cart section replacement or an account refresh
    // recalculates it, including while a saved voucher is applied or removed.

    if (this.isPreview && snapshot.appliedReward) this.applyConfirmedReward(snapshot.appliedReward);

    this.dispatchEvent(new CustomEvent('sigma:loyalty-snapshot', {
      bubbles: true,
      detail: snapshot,
    }));
    restoreRewardViewport(viewportAnchor);
  }

  resolveVoucherOwnership(savedVouchers = this.lastSavedVouchers) {
    const appliedVouchers = readAppliedVouchers(this.dataset);
    return resolveVoucherOwnershipDisplay({
      authenticated: this.dataset.authenticated === 'true',
      savedVouchers,
      appliedVouchers,
      appliedValueMinor: Number(this.dataset.appliedVoucherValueMinor),
      cartTotalMinor: safeNonNegativeInteger(this.dataset.cartTotalMinor),
    });
  }

  applyVoucherOwnershipDisplay(savedVouchers = null) {
    const ownership = this.resolveVoucherOwnership(savedVouchers);
    const summary = this.closest('.cart-summary');
    const appliedRow = summary?.querySelector('[data-loyalty-applied-row]');
    if (appliedRow) appliedRow.hidden = ownership.ownedValueMinor === 0;
    setText(summary, '[data-loyalty-applied-saving]', ownership.ownedValueMinor > 0
      ? `-${formatMoney(ownership.ownedValueMinor, this.dataset.currencyCode)}` : '');
    setText(summary, '[data-loyalty-estimated-total]', formatMoney(
      ownership.displayTotalMinor,
      this.dataset.currencyCode,
    ));

    const bag = this.closest('[data-cart-page], [data-cart-drawer]');
    const shipping = bag?.querySelector('[data-cart-shipping]');
    if (shipping) {
      shipping.dataset.cartShippingVoucherOwnership = ownership.withheldValueMinor > 0
        ? 'withheld' : 'verified';
      const confirmedDiscountTotal = safeNonNegativeInteger(
        shipping.dataset.cartShippingConfirmedDiscountTotal,
      );
      shipping.dataset.cartShippingTotal = String(ownership.displayTotalMinor);
      shipping.dataset.cartShippingDiscountTotal = String(Math.max(
        0,
        confirmedDiscountTotal - ownership.withheldValueMinor,
      ));
      syncTierShipping(bag);
    }
    this.voucherOwnership = ownership;
    return ownership;
  }

  renderNativeVoucherRewards(rewards, ownership = this.resolveVoucherOwnership()) {
    const list = this.querySelector('[data-loyalty-reward-list]');
    if (!list) return;
    list.replaceChildren();
    const catalogue = parseVoucherCatalogue(rewards) || [];
    this.lastNativeVoucherRewards = catalogue;
    this.appliedVouchers = ownership.ownedVouchers;
    const valueMinor = this.appliedVouchers
      .reduce((sum, voucher) => sum + voucher.valueMinor, 0);
    this.appliedVoucher = this.appliedVouchers.length > 0
      ? {
        code: this.appliedVouchers.length === 1 ? this.appliedVouchers[0].code : '',
        valueMinor,
      }
      : null;
    this.appliedReward = this.appliedVoucher;
    this.hasNativeVoucherCatalogue = catalogue.length > 0;
    const entries = [
      ...this.appliedVouchers.map((voucher, index) => ({
        ...voucher,
        id: `applied-${index}`,
        applied: true,
      })),
      ...catalogue.map((entry) => ({ ...entry, applied: false })),
    ];
    for (const entry of entries) {
      const selected = entry.applied === true;
      const points = entry.points || null;
      const item = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'loyalty-summary__reward loyalty-summary__voucher-reward';
      button.disabled = !selected || this.removingVoucher === true;
      button.setAttribute('aria-pressed', String(selected));
      button.dataset.voucherAvailable = String(selected);
      if (selected) {
        button.dataset.loyaltyReadonlyRemove = entry.code;
        button.setAttribute('aria-label', `Remove ${formatRewardMoney(entry.valueMinor, this.dataset.currencyCode)} reward from bag. Spent points are not refunded.`);
      } else {
        button.setAttribute('aria-label', `${formatRewardMoney(entry.valueMinor, this.dataset.currencyCode)} reward${points ? ` costs ${numberFormatter.format(points)} points` : ''}. Claiming is not available.`);
      }
      const icon = document.createElement('img');
      icon.className = 'loyalty-summary__reward-icon';
      icon.src = this.dataset.rewardIconUrl || '';
      icon.alt = '';
      icon.width = 24;
      icon.height = 24;
      const copy = document.createElement('span');
      copy.className = 'loyalty-summary__reward-copy';
      const cost = document.createElement('span');
      cost.className = 'loyalty-summary__reward-points';
      cost.textContent = points ? `${numberFormatter.format(points)}pts` : '';
      const value = document.createElement('span');
      value.className = 'loyalty-summary__reward-value';
      value.textContent = selected
        ? `${formatRewardMoney(entry.valueMinor, this.dataset.currencyCode)} applied`
        : `${formatRewardMoney(entry.valueMinor, this.dataset.currencyCode)} off`;
      copy.append(cost, value);
      button.append(icon, copy);
      item.append(button);
      list.append(item);
    }
    const remove = this.querySelector('[data-loyalty-remove]');
    if (remove) remove.hidden = this.appliedVouchers.length !== 1;
    setText(this, '[data-loyalty-reward-label]', this.appliedVoucher
      ? `${formatRewardMoney(this.appliedVoucher.valueMinor, this.dataset.currencyCode)} ${
        this.appliedVouchers.length === 1 ? 'reward' : 'rewards'
      } applied`
      : 'Choose amount to redeem');
    const note = this.querySelector('[data-loyalty-applied-note]');
    if (note) note.hidden = !this.appliedVoucher;
  }

  async removeAppliedVoucher(inputCode) {
    if (this.voucherRedemptionManaged || this.removingVoucher || !this.appliedVoucher) return;
    const requestedCode = typeof inputCode === 'string' && inputCode
      ? inputCode
      : this.appliedVouchers.length === 1 ? this.appliedVouchers[0].code : '';
    const appliedVoucher = this.appliedVouchers.find((voucher) => (
      codeIdentity(voucher.code) === codeIdentity(requestedCode)
    ));
    if (!appliedVoucher) return;
    this.removingVoucher = true;
    this.renderNativeVoucherRewards(this.lastNativeVoucherRewards);
    const panel = this.querySelector('[data-loyalty-panel]');
    if (panel) panel.dataset.state = 'removing';
    try {
      const adapter = createShopifyIssuedVoucherAdapter({
        eventTarget: window,
        fetchImpl: window.fetch.bind(window),
        getCartPageSectionId: () => document.querySelector('[data-route-entry-root="cart"]')
          ?.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '') || '',
        location: window.location,
        routesRoot: window.Shopify?.routes?.root,
      });
      const result = await adapter.remove(appliedVoucher.code);
      if (!['removed', 'unchanged'].includes(result.status)) {
        throw new Error('The bag update could not be confirmed.');
      }
      writeAppliedVouchers(this.dataset, readAppliedVouchers(this.dataset).filter((voucher) => (
        codeIdentity(voucher.code) !== codeIdentity(appliedVoucher.code)
      )));
      this.renderNativeVoucherRewards(this.lastNativeVoucherRewards);
      this.setExpanded(true);
    } catch {
      const feedback = this.querySelector('[data-loyalty-feedback]');
      const retry = this.querySelector('[data-loyalty-retry]');
      if (feedback) feedback.hidden = false;
      if (retry) retry.hidden = true;
      setText(this, '[data-loyalty-error]', 'We could not confirm the reward was removed. Review your bag before checking out.');
    } finally {
      this.removingVoucher = false;
      this.renderNativeVoucherRewards(this.lastNativeVoucherRewards);
      if (panel && panel.dataset.state === 'removing') panel.dataset.state = 'expanded';
    }
  }

  setExpanded(expanded, { animate = false, anchor = animate } = {}) {
    const viewportAnchor = anchor ? captureRewardViewport(this, { reveal: true }) : null;
    const toggle = this.querySelector('[data-loyalty-toggle]');
    const rewards = this.querySelector('[data-loyalty-rewards]');
    const voucher = this.querySelector('[data-loyalty-redemption-slot]');
    const panel = this.querySelector('[data-loyalty-panel]');
    const earningMessage = this.querySelector('[data-loyalty-earning-message]');
    const closingContent = [rewards, voucher, earningMessage].filter(element => element && !element.hidden);
    const isExpanded = Boolean(expanded && toggle && !toggle.hidden);
    const motionSnapshot = captureRewardPanelMotion(this, panel);
    if (rewards) rewards.inert = false;
    if (voucher) voucher.inert = false;
    if (toggle) {
      toggle.setAttribute('aria-expanded', String(isExpanded));
      toggle.setAttribute('aria-label', isExpanded ? 'Close loyalty rewards' : 'Choose a loyalty reward');
    }
    setText(this, '[data-loyalty-toggle-label]', isExpanded ? 'Hide' : 'Use points');
    if (!isExpanded && closingContent.some(element => element.contains(document.activeElement))) {
      toggle?.focus({ preventScroll: true });
    }
    if (rewards) rewards.hidden = this.voucherRedemptionManaged || !isExpanded;
    if (voucher) voucher.hidden = !this.voucherRedemptionManaged || !isExpanded;
    if (panel && panel.dataset.state !== 'loading' && panel.dataset.state !== 'unavailable') {
      panel.dataset.state = this.appliedReward ? 'applied' : (isExpanded ? 'expanded' : 'collapsed');
    }
    const statusMessage = this.querySelector('[data-loyalty-status]');
    if (statusMessage) {
      const previousLabel = statusMessage.querySelector('.loyalty-summary__message-current') || statusMessage;
      const previousContent = document.createElement('span');
      for (const child of previousLabel.childNodes) previousContent.append(child.cloneNode(true));
      const previousLayers = [...statusMessage.querySelectorAll('.loyalty-summary__message-current, .loyalty-summary__message-outgoing')];
      const headingFrames = (previousLayers.length ? previousLayers : [previousLabel]).map(element => ({
        content: element.cloneNode(true),
        opacity: getComputedStyle(element).opacity,
        filter: getComputedStyle(element).filter,
      }));
      this.clearHeadingMotion();
      statusMessage.textContent = isExpanded ? this.expandedMessage : this.collapsedMessage;
      if (!isExpanded && this.earningPoints != null && this.subscriberBonusActive) {
        statusMessage.append(' ', this.createSubscriberBonusLabel());
      }
      if (animate && previousContent.textContent !== statusMessage.textContent
        && this.isConnected && typeof statusMessage.animate === 'function'
        && !window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
        this.animateHeading(statusMessage, headingFrames);
      }
    }
    if (earningMessage) {
      earningMessage.hidden = !isExpanded || this.earningPoints == null;
      earningMessage.textContent = this.earningPoints == null ? ''
        : `Earn ${numberFormatter.format(this.earningPoints)} points with this bag`;
      if (this.earningPoints != null && this.subscriberBonusActive) {
        earningMessage.append(' ', this.createSubscriberBonusLabel());
      }
      if (this.earningPoints != null && this.restedBonusPoints > 0) {
        earningMessage.append(document.createElement('br'),
          `Includes ${numberFormatter.format(this.restedBonusPoints)} Rested bonus points.`);
      }
    }
    revealRewardPanel(this, panel, motionSnapshot, {
      expanded: isExpanded, animate, anchor: viewportAnchor, closingContent,
    });
  }

  clearHeadingMotion() {
    for (const animation of this.headingAnimations || []) animation.cancel();
    this.headingAnimations = [];
    this.querySelectorAll('.loyalty-summary__message-outgoing').forEach(element => element.remove());
  }

  animateHeading(message, frames) {
    const current = document.createElement('span');
    current.className = 'loyalty-summary__message-current';
    current.append(...message.childNodes);
    message.append(current);
    const matching = frames.find(frame => frame.content.textContent === current.textContent);
    const timing = { easing: headingMotion.easing, fill: 'both' };
    const animations = frames.filter(frame => frame !== matching).map(frame => {
      const outgoing = document.createElement('span');
      outgoing.append(...frame.content.childNodes);
      outgoing.className = 'loyalty-summary__message-outgoing';
      outgoing.setAttribute('aria-hidden', 'true');
      message.append(outgoing);
      return outgoing.animate([
        { opacity: frame.opacity, filter: frame.filter },
        { opacity: 0, filter: `blur(${headingMotion.blurPx}px)` },
      ], { ...timing, duration: headingMotion.exitMs });
    });
    animations.push(current.animate([
        { opacity: matching?.opacity ?? 0, filter: matching?.filter ?? `blur(${headingMotion.blurPx}px)` },
        { opacity: 1, filter: 'blur(0px)' },
      ], { ...timing, duration: headingMotion.enterMs }));
    this.headingAnimations = animations;
    Promise.all(animations.map(animation => animation.finished)).catch(() => {}).then(() => {
      if (this.headingAnimations !== animations) return;
      this.clearHeadingMotion();
    });
  }

  createSubscriberBonusLabel() {
    const label = document.createElement('span');
    label.className = 'loyalty-summary__subscriber-bonus';
    label.setAttribute('aria-label', `${this.subscriberBonusPercent} percent subscriber bonus on purchase points only`);
    const separator = document.createElement('span');
    separator.className = 'loyalty-summary__bonus-separator';
    separator.setAttribute('aria-hidden', 'true');
    separator.textContent = '·';
    label.append(separator, `\u00a0+${this.subscriberBonusPercent}% bonus active`);
    return label;
  }

  renderRewards(rewards, currencyCode, redemptionAvailable) {
    const list = this.querySelector('[data-loyalty-reward-list]');
    if (!list) return 0;
    list.replaceChildren();
    this.rewards = new Map();

    const affordableRewards = redemptionAvailable && Array.isArray(rewards)
      ? rewards.filter((reward) => reward?.affordable === true)
      : [];

    affordableRewards.forEach((reward) => {
      const code = String(reward.code || '');
      if (!code) return;
      const safeReward = {
        code,
        points: safeNonNegativeInteger(reward.points),
        discountMinor: safeNonNegativeInteger(reward.discountMinor),
      };
      this.rewards.set(code, safeReward);

      const item = document.createElement('li');
      const button = document.createElement('button');
      button.className = 'loyalty-summary__reward';
      button.type = 'button';
      button.dataset.loyaltyRewardCode = code;
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute(
        'aria-label',
        `Redeem ${numberFormatter.format(safeReward.points)} points for ${formatRewardMoney(safeReward.discountMinor, currencyCode)} off`,
      );

      const icon = document.createElement('img');
      icon.className = 'loyalty-summary__reward-icon';
      icon.src = this.dataset.rewardIconUrl || '';
      icon.alt = '';
      icon.width = 24;
      icon.height = 24;

      const copy = document.createElement('span');
      copy.className = 'loyalty-summary__reward-copy';
      const points = document.createElement('span');
      points.className = 'loyalty-summary__reward-points';
      points.textContent = `${numberFormatter.format(safeReward.points)}pts`;
      const value = document.createElement('span');
      value.className = 'loyalty-summary__reward-value';
      value.textContent = `${formatRewardMoney(safeReward.discountMinor, currencyCode)} off`;
      copy.append(points, value);
      button.append(icon, copy);
      item.append(button);
      list.append(item);
    });

    return this.rewards.size;
  }

  requestReward(code) {
    const reward = this.rewards?.get(String(code || ''));
    if (!reward) return;
    this.hideFeedback();
    this.setInteractionState('applying');
    setText(this, '[data-loyalty-status]', `Applying ${formatRewardMoney(reward.discountMinor, this.dataset.currencyCode)} reward`);
    if (this.isPreview) {
      this.applyConfirmedReward({
        ...reward,
        remainingPoints: Math.max(0, safeNonNegativeInteger(this.approvedPoints) - reward.points),
        estimatedTotalMinor: Math.max(
          0,
          safeNonNegativeInteger(this.dataset.cartTotalMinor) - reward.discountMinor,
        ),
      });
      return;
    }
    const handled = !this.dispatchEvent(new CustomEvent('sigma:loyalty-reward-request', {
      bubbles: true,
      cancelable: true,
      detail: { reward, summary: this },
    }));
    if (!handled) this.showRedemptionUnavailable();
  }

  requestRemoveReward() {
    if (!this.appliedReward) return;
    if (!this.isPreview && !this.voucherRedemptionManaged && this.appliedVoucher) {
      this.removeAppliedVoucher();
      return;
    }
    this.hideFeedback();
    this.setInteractionState('removing');
    setText(this, '[data-loyalty-status]', 'Removing reward');

    if (this.isPreview) {
      this.clearConfirmedReward();
      return;
    }

    const handled = !this.dispatchEvent(new CustomEvent('sigma:loyalty-reward-remove-request', {
      bubbles: true,
      cancelable: true,
      detail: { reward: this.appliedReward, summary: this },
    }));
    if (!handled) this.showRemovalUnavailable();
  }

  setInteractionState(state) {
    const isBusy = state === 'applying' || state === 'removing';
    const panel = this.querySelector('[data-loyalty-panel]');
    const toggle = this.querySelector('[data-loyalty-toggle]');
    const remove = this.querySelector('[data-loyalty-remove]');
    if (panel) panel.dataset.state = state;
    if (toggle) toggle.disabled = isBusy;
    if (remove) remove.disabled = isBusy;
    this.querySelectorAll('[data-loyalty-reward-code]').forEach((button) => {
      button.disabled = isBusy;
    });
    this.setAttribute('aria-busy', String(isBusy));
  }

  showRedemptionUnavailable() {
    this.setInteractionState(this.appliedReward ? 'applied' : 'expanded');
    this.setExpanded(true);
    const feedback = this.querySelector('[data-loyalty-feedback]');
    const retry = this.querySelector('[data-loyalty-retry]');
    if (feedback) feedback.hidden = false;
    if (retry) retry.hidden = true;
    setText(
      this,
      '[data-loyalty-error]',
      this.appliedReward
        ? 'We could not change this reward. Your current reward remains applied and you can still check out normally.'
        : 'Rewards are unavailable right now. Your points have not been used and you can still check out normally.',
    );
  }

  showRemovalUnavailable() {
    this.setInteractionState('applied');
    this.setExpanded(true);
    const feedback = this.querySelector('[data-loyalty-feedback]');
    const retry = this.querySelector('[data-loyalty-retry]');
    if (feedback) feedback.hidden = false;
    if (retry) retry.hidden = true;
    setText(
      this,
      '[data-loyalty-error]',
      'We could not remove this reward. It remains applied and you can still check out normally.',
    );
  }

  hideFeedback() {
    const feedback = this.querySelector('[data-loyalty-feedback]');
    if (feedback) feedback.hidden = true;
  }

  applyConfirmedReward(result) {
    const reward = this.rewards?.get(String(result?.code || result?.rewardCode || ''));
    const discountMinor = safeNonNegativeInteger(result?.discountMinor, reward?.discountMinor || 0);
    if (discountMinor <= 0) return false;

    const appliedCode = String(result?.code || result?.rewardCode || reward?.code || '');
    this.querySelectorAll('[data-loyalty-reward-code]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.loyaltyRewardCode === appliedCode));
    });

    const spentPoints = safeNonNegativeInteger(result?.points, reward?.points || 0);
    const remainingPoints = safeNonNegativeInteger(
      result?.remainingPoints,
      safeNonNegativeInteger(this.approvedPoints) - spentPoints,
    );
    this.expandedMessage = `${numberFormatter.format(remainingPoints)} points available`;
    this.appliedReward = {
      code: appliedCode,
      points: spentPoints,
      discountMinor,
      remainingPoints,
    };
    setText(this, '[data-loyalty-reward-label]', `${formatMoney(discountMinor, this.dataset.currencyCode)} reward applied`);
    const remove = this.querySelector('[data-loyalty-remove]');
    if (remove) remove.hidden = false;
    this.setInteractionState('applied');
    this.setExpanded(true);

    const summary = this.closest('.cart-summary');
    const appliedRow = summary?.querySelector('[data-loyalty-applied-row]');
    const cartTotalMinor = safeNonNegativeInteger(this.dataset.cartTotalMinor);
    const estimatedTotalMinor = safeNonNegativeInteger(
      result?.estimatedTotalMinor,
      Math.max(0, cartTotalMinor - discountMinor),
    );
    if (appliedRow) appliedRow.hidden = false;
    setText(summary, '[data-loyalty-applied-saving]', `-${formatMoney(discountMinor, this.dataset.currencyCode)}`);
    setText(summary, '[data-loyalty-estimated-total]', formatMoney(estimatedTotalMinor, this.dataset.currencyCode));
    return true;
  }

  clearConfirmedReward(result = {}) {
    this.querySelectorAll('[data-loyalty-reward-code]').forEach((button) => {
      button.setAttribute('aria-pressed', 'false');
    });
    this.appliedReward = null;
    this.expandedMessage = `${numberFormatter.format(Math.max(0, this.approvedPoints || 0))} points available`;
    setText(this, '[data-loyalty-reward-label]', 'Choose amount to redeem');
    const remove = this.querySelector('[data-loyalty-remove]');
    if (remove) remove.hidden = true;
    this.setInteractionState('expanded');
    this.setExpanded(true);
    const summary = this.closest('.cart-summary');
    const appliedRow = summary?.querySelector('[data-loyalty-applied-row]');
    if (appliedRow) appliedRow.hidden = true;
    const totalMinor = safeNonNegativeInteger(result?.estimatedTotalMinor, this.dataset.cartTotalMinor);
    setText(summary, '[data-loyalty-estimated-total]', formatMoney(totalMinor, this.dataset.currencyCode));
    setText(this, '[data-loyalty-status]', `Reward removed · ${this.expandedMessage}`);
  }
}

if (!customElements.get('sigma-loyalty-summary')) {
  customElements.define('sigma-loyalty-summary', SigmaLoyaltySummary);
}
